import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../../../store/store';
import { getRequest, putRequest, deleteRequest, apiSuccessHandler, apiErrorHandler, setApiResult } from '../../core/bffApiModule'
import * as CONSTANTS from '../../../constants/constants';
import * as Model from './qualificationModel';
import * as Utils from '../../../utils/utils';
import * as messagesUtils from '../../../utils/messagesUtils';

// state初期化
const initialState: Model.State = {
  dataList: []
  , dataRow: {
    key: ""
    , processType: ""
    , ccusFlag: CONSTANTS.CCUS_FLAG.OFF
    , originalQualificationId: ""
    , originalQualificationNo: ""
    , originalQualificationName: ""
    , allowanceType: CONSTANTS.ALLOWANCE_TYPE.DAILY.code
    , allowanceInspectionType: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
    , allowanceInspectionTime: 1
    , monthlyAllowanceInspectionDays: 0
    , allowanceAmount: 0
    , ceilingDailyAllowanceInspectionDays: NaN
    , ceilingDailyAllowanceAmount: NaN
    , deadlineExist: CONSTANTS.EXIST_DEADLINE.EXIST.code
    , deadlineStartDate: ""
    , deadlineEndDate: ""
    , levelRangeTo: CONSTANTS.HIERARCHY_LEVEL_MAX // 初期値は1～5次とする
    , remarks1: ""
    , remarks2: ""
    , numberOfCompanies: 0
    , numberOfPeople: 0
    , updateDatetime: ""
    , minCertDate: ""
    , maxCertDate: ""
    , maxCertCancelDate: ""
    , ccusAllowanceType1: CONSTANTS.ALLOWANCE_TYPE.NONE.code // 初期値はなしとする（独自資格とは異なる）
    , ccusAllowanceInspectionType1: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
    , ccusAllowanceInspectionTime1: 1
    , ccusMonthlyAllowanceInspectionDays1: 0
    , ccusAllowanceAmount1: 0
    , ccusCeilingDailyAllowanceInspectionDays1: NaN
    , ccusCeilingDailyAllowanceAmount1: NaN
    , ccusLevelRangeTo1: CONSTANTS.HIERARCHY_LEVEL_MAX // 初期値は1～5次とする
    , ccusLevelUpdateDatetime1: ""
    , ccusAllowanceType2: CONSTANTS.ALLOWANCE_TYPE.NONE.code // 初期値はなしとする（独自資格とは異なる）
    , ccusAllowanceInspectionType2: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
    , ccusAllowanceInspectionTime2: 1
    , ccusMonthlyAllowanceInspectionDays2: 0
    , ccusAllowanceAmount2: 0
    , ccusCeilingDailyAllowanceInspectionDays2: NaN
    , ccusCeilingDailyAllowanceAmount2: NaN
    , ccusLevelRangeTo2: CONSTANTS.HIERARCHY_LEVEL_MAX // 初期値は1～5次とする
    , ccusLevelUpdateDatetime2: ""
    , ccusAllowanceType3: CONSTANTS.ALLOWANCE_TYPE.NONE.code // 初期値はなしとする（独自資格とは異なる）
    , ccusAllowanceInspectionType3: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
    , ccusAllowanceInspectionTime3: 1
    , ccusMonthlyAllowanceInspectionDays3: 0
    , ccusAllowanceAmount3: 0
    , ccusCeilingDailyAllowanceInspectionDays3: NaN
    , ccusCeilingDailyAllowanceAmount3: NaN
    , ccusLevelRangeTo3: CONSTANTS.HIERARCHY_LEVEL_MAX // 初期値は1～5次とする
    , ccusLevelUpdateDatetime3: ""
    , ccusAllowanceType4: CONSTANTS.ALLOWANCE_TYPE.NONE.code // 初期値はなしとする（独自資格とは異なる）
    , ccusAllowanceInspectionType4: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
    , ccusAllowanceInspectionTime4: 1
    , ccusMonthlyAllowanceInspectionDays4: 0
    , ccusAllowanceAmount4: 0
    , ccusCeilingDailyAllowanceInspectionDays4: NaN
    , ccusCeilingDailyAllowanceAmount4: NaN
    , ccusLevelRangeTo4: CONSTANTS.HIERARCHY_LEVEL_MAX // 初期値は1～5次とする
    , ccusLevelUpdateDatetime4: ""
  }
  , total: 0
  , ccusFlagSelected: false
  , criteria: {
    originalQualificationNo: ""
    , originalQualificationName: ""
    , originalQualificationId: ""
    , sortItem: ""
    , sortOrder: ""
    , offset: 0
    , limit: 10
  }
  , loadingFlg: false
};

// スライサー （state更新アクションの実装）
export const qualificationSlice = createSlice({
  name: 'primeQualification',
  initialState,
  reducers: {
    initCriteria: (state) => {
      let criteria = {
        originalQualificationNo: ""
        , originalQualificationName: ""
        , originalQualificationId: ""
        , sortItem: ""
        , sortOrder: ""
        , offset: 0
        , limit: 10
      }
      state.criteria = criteria;
    }
    /** 検索条件のstate更新 */
    , setQualificationCriteria: (state, action: PayloadAction<Model.Criteria>) => {
      let qualificationCriteria: Model.Criteria = {
        originalQualificationNo: action.payload.originalQualificationNo,
        originalQualificationName: action.payload.originalQualificationName,
        originalQualificationId: action.payload.originalQualificationId,
        sortItem: action.payload.sortItem,
        sortOrder: action.payload.sortOrder,
        offset: action.payload.offset,
        limit: action.payload.limit
      }
      return Object.assign({}, state, { criteria: qualificationCriteria })
    },
    /** 一覧画面から詳細画面遷移時のstate更新 */
    prepareMoveToDetail: (state, action: PayloadAction<Model.dataRow>) => {
      const rec: Model.dataRow = {
        key: ""
        , processType: ""
        , ccusFlag: CONSTANTS.CCUS_FLAG.OFF
        , originalQualificationId: action.payload.originalQualificationId
        , originalQualificationNo: ""
        , originalQualificationName: ""
        , allowanceType: CONSTANTS.ALLOWANCE_TYPE.DAILY.code
        , allowanceInspectionType: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
        , allowanceInspectionTime: 1
        , monthlyAllowanceInspectionDays: 0
        , allowanceAmount: 0
        , ceilingDailyAllowanceInspectionDays: NaN
        , ceilingDailyAllowanceAmount: NaN
        , deadlineExist: ""
        , deadlineStartDate: ""
        , deadlineEndDate: ""
        , levelRangeTo: NaN
        , remarks1: ""
        , remarks2: ""
        , numberOfCompanies: 0
        , numberOfPeople: 0
        , updateDatetime: ""
        , minCertDate: ""
        , maxCertDate: ""
        , maxCertCancelDate: ""
        , ccusAllowanceType1: CONSTANTS.ALLOWANCE_TYPE.DAILY.code
        , ccusAllowanceInspectionType1: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
        , ccusAllowanceInspectionTime1: 1
        , ccusMonthlyAllowanceInspectionDays1: 0
        , ccusAllowanceAmount1: 0
        , ccusCeilingDailyAllowanceInspectionDays1: NaN
        , ccusCeilingDailyAllowanceAmount1: NaN
        , ccusLevelRangeTo1: NaN
        , ccusLevelUpdateDatetime1: ""
        , ccusAllowanceType2: CONSTANTS.ALLOWANCE_TYPE.DAILY.code
        , ccusAllowanceInspectionType2: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
        , ccusAllowanceInspectionTime2: 1
        , ccusMonthlyAllowanceInspectionDays2: 0
        , ccusAllowanceAmount2: 0
        , ccusCeilingDailyAllowanceInspectionDays2: NaN
        , ccusCeilingDailyAllowanceAmount2: NaN
        , ccusLevelRangeTo2: NaN
        , ccusLevelUpdateDatetime2: ""
        , ccusAllowanceType3: CONSTANTS.ALLOWANCE_TYPE.DAILY.code
        , ccusAllowanceInspectionType3: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
        , ccusAllowanceInspectionTime3: 1
        , ccusMonthlyAllowanceInspectionDays3: 0
        , ccusAllowanceAmount3: 0
        , ccusCeilingDailyAllowanceInspectionDays3: NaN
        , ccusCeilingDailyAllowanceAmount3: NaN
        , ccusLevelRangeTo3: NaN
        , ccusLevelUpdateDatetime3: ""
        , ccusAllowanceType4: CONSTANTS.ALLOWANCE_TYPE.DAILY.code
        , ccusAllowanceInspectionType4: CONSTANTS.ALLOWANCE_INSPECTION_TYPE.EXIST.code
        , ccusAllowanceInspectionTime4: 1
        , ccusMonthlyAllowanceInspectionDays4: 0
        , ccusAllowanceAmount4: 0
        , ccusCeilingDailyAllowanceInspectionDays4: NaN
        , ccusCeilingDailyAllowanceAmount4: NaN
        , ccusLevelRangeTo4: NaN
        , ccusLevelUpdateDatetime4: ""
      };
      return Object.assign({}, state, { dataRow: rec })
    },
    /** 一覧画面描画時のstate更新 */
    setDataListOfQualificationMaster: (state, action: PayloadAction<Model.getDataList.Response>) => {

      let qualificationList: Array<Model.dataRow> = [];

      action.payload.originalQualificationList.forEach((apiDataRow: Model.dataRow) => {

        const rec: Model.dataRow = {
          key: apiDataRow.originalQualificationId
          , processType: ""
          , ccusFlag: apiDataRow.ccusFlag
          , originalQualificationId: apiDataRow.originalQualificationId
          , originalQualificationNo: apiDataRow.originalQualificationNo
          , originalQualificationName: apiDataRow.originalQualificationName
          , allowanceType: apiDataRow.allowanceType
          , allowanceInspectionType: apiDataRow.allowanceInspectionType
          , allowanceInspectionTime: apiDataRow.allowanceInspectionTime
          , monthlyAllowanceInspectionDays: apiDataRow.monthlyAllowanceInspectionDays
          , allowanceAmount: apiDataRow.allowanceAmount
          , ceilingDailyAllowanceInspectionDays: apiDataRow.ceilingDailyAllowanceInspectionDays
          , ceilingDailyAllowanceAmount: apiDataRow.ceilingDailyAllowanceAmount
          , deadlineExist: apiDataRow.deadlineExist
          , deadlineStartDate: apiDataRow.deadlineStartDate
          , deadlineEndDate: apiDataRow.deadlineEndDate
          , levelRangeTo: apiDataRow.levelRangeTo
          , remarks1: apiDataRow.remarks1
          , remarks2: apiDataRow.remarks2
          , numberOfCompanies: apiDataRow.numberOfCompanies
          , numberOfPeople: apiDataRow.numberOfPeople
          , updateDatetime: apiDataRow.updateDatetime
          , minCertDate: apiDataRow.minCertDate
          , maxCertDate: apiDataRow.maxCertDate
          , maxCertCancelDate: apiDataRow.maxCertCancelDate
          , ccusAllowanceType1: apiDataRow.ccusAllowanceType1
          , ccusAllowanceInspectionType1: apiDataRow.ccusAllowanceInspectionType1
          , ccusAllowanceInspectionTime1: apiDataRow.ccusAllowanceInspectionTime1
          , ccusMonthlyAllowanceInspectionDays1: apiDataRow.ccusMonthlyAllowanceInspectionDays1
          , ccusAllowanceAmount1: apiDataRow.ccusAllowanceAmount1
          , ccusCeilingDailyAllowanceInspectionDays1: apiDataRow.ccusCeilingDailyAllowanceInspectionDays1
          , ccusCeilingDailyAllowanceAmount1: apiDataRow.ccusCeilingDailyAllowanceAmount1
          , ccusLevelRangeTo1: apiDataRow.ccusLevelRangeTo1
          , ccusLevelUpdateDatetime1: apiDataRow.ccusLevelUpdateDatetime1
          , ccusAllowanceType2: apiDataRow.ccusAllowanceType2
          , ccusAllowanceInspectionType2: apiDataRow.ccusAllowanceInspectionType2
          , ccusAllowanceInspectionTime2: apiDataRow.ccusAllowanceInspectionTime2
          , ccusMonthlyAllowanceInspectionDays2: apiDataRow.ccusMonthlyAllowanceInspectionDays2
          , ccusAllowanceAmount2: apiDataRow.ccusAllowanceAmount2
          , ccusCeilingDailyAllowanceInspectionDays2: apiDataRow.ccusCeilingDailyAllowanceInspectionDays2
          , ccusCeilingDailyAllowanceAmount2: apiDataRow.ccusCeilingDailyAllowanceAmount2
          , ccusLevelRangeTo2: apiDataRow.ccusLevelRangeTo2
          , ccusLevelUpdateDatetime2: apiDataRow.ccusLevelUpdateDatetime2
          , ccusAllowanceType3: apiDataRow.ccusAllowanceType3
          , ccusAllowanceInspectionType3: apiDataRow.ccusAllowanceInspectionType3
          , ccusAllowanceInspectionTime3: apiDataRow.ccusAllowanceInspectionTime3
          , ccusMonthlyAllowanceInspectionDays3: apiDataRow.ccusMonthlyAllowanceInspectionDays3
          , ccusAllowanceAmount3: apiDataRow.ccusAllowanceAmount3
          , ccusCeilingDailyAllowanceInspectionDays3: apiDataRow.ccusCeilingDailyAllowanceInspectionDays3
          , ccusCeilingDailyAllowanceAmount3: apiDataRow.ccusCeilingDailyAllowanceAmount3
          , ccusLevelRangeTo3: apiDataRow.ccusLevelRangeTo3
          , ccusLevelUpdateDatetime3: apiDataRow.ccusLevelUpdateDatetime3
          , ccusAllowanceType4: apiDataRow.ccusAllowanceType4
          , ccusAllowanceInspectionType4: apiDataRow.ccusAllowanceInspectionType4
          , ccusAllowanceInspectionTime4: apiDataRow.ccusAllowanceInspectionTime4
          , ccusMonthlyAllowanceInspectionDays4: apiDataRow.ccusMonthlyAllowanceInspectionDays4
          , ccusAllowanceAmount4: apiDataRow.ccusAllowanceAmount4
          , ccusCeilingDailyAllowanceInspectionDays4: apiDataRow.ccusCeilingDailyAllowanceInspectionDays4
          , ccusCeilingDailyAllowanceAmount4: apiDataRow.ccusCeilingDailyAllowanceAmount4
          , ccusLevelRangeTo4: apiDataRow.ccusLevelRangeTo4
          , ccusLevelUpdateDatetime4: apiDataRow.ccusLevelUpdateDatetime4
        };
        qualificationList.push(rec);
      });

      return Object.assign({}, state, { dataList: qualificationList }, { total: action.payload.total })
    },
    /** 詳細画面描画時のstate更新 */
    setDataRowOfQualificationMaster: (state, action: PayloadAction<Model.getDataList.Response>) => {
      const payloadData = action.payload.originalQualificationList[0];
      const rec: Model.dataRow = {
        key: payloadData.originalQualificationId
        , processType: ""
        , ccusFlag: payloadData.ccusFlag
        , originalQualificationId: payloadData.originalQualificationId
        , originalQualificationNo: payloadData.originalQualificationNo
        , originalQualificationName: payloadData.originalQualificationName
        , allowanceType: payloadData.allowanceType
        , allowanceInspectionType: payloadData.allowanceInspectionType
        , allowanceInspectionTime: payloadData.allowanceInspectionTime
        , monthlyAllowanceInspectionDays: payloadData.monthlyAllowanceInspectionDays
        , allowanceAmount: payloadData.allowanceAmount
        , ceilingDailyAllowanceInspectionDays: payloadData.ceilingDailyAllowanceInspectionDays
        , ceilingDailyAllowanceAmount: payloadData.ceilingDailyAllowanceAmount
        , deadlineExist: payloadData.deadlineExist
        , deadlineStartDate: payloadData.deadlineStartDate
        , deadlineEndDate: payloadData.deadlineEndDate
        , levelRangeTo: payloadData.levelRangeTo
        , remarks1: payloadData.remarks1
        , remarks2: payloadData.remarks2
        , numberOfCompanies: payloadData.numberOfCompanies
        , numberOfPeople: payloadData.numberOfPeople
        , updateDatetime: payloadData.updateDatetime
        , minCertDate: payloadData.minCertDate
        , maxCertDate: payloadData.maxCertDate
        , maxCertCancelDate: payloadData.maxCertCancelDate
        , ccusAllowanceType1: payloadData.ccusAllowanceType1
        , ccusAllowanceInspectionType1: payloadData.ccusAllowanceInspectionType1
        , ccusAllowanceInspectionTime1: payloadData.ccusAllowanceInspectionTime1
        , ccusMonthlyAllowanceInspectionDays1: payloadData.ccusMonthlyAllowanceInspectionDays1
        , ccusAllowanceAmount1: payloadData.ccusAllowanceAmount1
        , ccusCeilingDailyAllowanceInspectionDays1: payloadData.ccusCeilingDailyAllowanceInspectionDays1
        , ccusCeilingDailyAllowanceAmount1: payloadData.ccusCeilingDailyAllowanceAmount1
        , ccusLevelRangeTo1: payloadData.ccusLevelRangeTo1
        , ccusLevelUpdateDatetime1: payloadData.ccusLevelUpdateDatetime1
        , ccusAllowanceType2: payloadData.ccusAllowanceType2
        , ccusAllowanceInspectionType2: payloadData.ccusAllowanceInspectionType2
        , ccusAllowanceInspectionTime2: payloadData.ccusAllowanceInspectionTime2
        , ccusMonthlyAllowanceInspectionDays2: payloadData.ccusMonthlyAllowanceInspectionDays2
        , ccusAllowanceAmount2: payloadData.ccusAllowanceAmount2
        , ccusCeilingDailyAllowanceInspectionDays2: payloadData.ccusCeilingDailyAllowanceInspectionDays2
        , ccusCeilingDailyAllowanceAmount2: payloadData.ccusCeilingDailyAllowanceAmount2
        , ccusLevelRangeTo2: payloadData.ccusLevelRangeTo2
        , ccusLevelUpdateDatetime2: payloadData.ccusLevelUpdateDatetime2
        , ccusAllowanceType3: payloadData.ccusAllowanceType3
        , ccusAllowanceInspectionType3: payloadData.ccusAllowanceInspectionType3
        , ccusAllowanceInspectionTime3: payloadData.ccusAllowanceInspectionTime3
        , ccusMonthlyAllowanceInspectionDays3: payloadData.ccusMonthlyAllowanceInspectionDays3
        , ccusAllowanceAmount3: payloadData.ccusAllowanceAmount3
        , ccusCeilingDailyAllowanceInspectionDays3: payloadData.ccusCeilingDailyAllowanceInspectionDays3
        , ccusCeilingDailyAllowanceAmount3: payloadData.ccusCeilingDailyAllowanceAmount3
        , ccusLevelRangeTo3: payloadData.ccusLevelRangeTo3
        , ccusLevelUpdateDatetime3: payloadData.ccusLevelUpdateDatetime3
        , ccusAllowanceType4: payloadData.ccusAllowanceType4
        , ccusAllowanceInspectionType4: payloadData.ccusAllowanceInspectionType4
        , ccusAllowanceInspectionTime4: payloadData.ccusAllowanceInspectionTime4
        , ccusMonthlyAllowanceInspectionDays4: payloadData.ccusMonthlyAllowanceInspectionDays4
        , ccusAllowanceAmount4: payloadData.ccusAllowanceAmount4
        , ccusCeilingDailyAllowanceInspectionDays4: payloadData.ccusCeilingDailyAllowanceInspectionDays4
        , ccusCeilingDailyAllowanceAmount4: payloadData.ccusCeilingDailyAllowanceAmount4
        , ccusLevelRangeTo4: payloadData.ccusLevelRangeTo4
        , ccusLevelUpdateDatetime4: payloadData.ccusLevelUpdateDatetime4
      };

      // 初期値を設定（独自資格の場合、CCUS資格の項目に初期値を設定。CCUS資格の場合、独自資格の項目に初期値を設定）
      if (rec.ccusFlag === CONSTANTS.CCUS_FLAG.OFF) {
        // CCUSの初期値を設定
        for (let i = CONSTANTS.CCUS_LEVEL_MIN; i <= CONSTANTS.CCUS_LEVEL_MAX; i++) {
          rec["ccusAllowanceType" + i] = initialState.dataRow["ccusAllowanceType" + i];
          rec["ccusAllowanceInspectionType" + i] = initialState.dataRow["ccusAllowanceInspectionType" + i];
          rec["ccusAllowanceInspectionTime" + i] = initialState.dataRow["ccusAllowanceInspectionTime" + i];
          rec["ccusMonthlyAllowanceInspectionDays" + i] = initialState.dataRow["ccusMonthlyAllowanceInspectionDays" + i];
          rec["ccusCeilingDailyAllowanceInspectionDays" + i] = initialState.dataRow["ccusCeilingDailyAllowanceInspectionDays" + i];
          rec["ccusCeilingDailyAllowanceAmount" + i] = initialState.dataRow["ccusCeilingDailyAllowanceAmount" + i];
          rec["ccusLevelRangeTo" + i] = initialState.dataRow["ccusLevelRangeTo" + i];
          rec["ccusLevelUpdateDatetime" + i] = initialState.dataRow["ccusLevelUpdateDatetime" + i];
        }
      } else {
        rec.allowanceType = initialState.dataRow.allowanceType;
        rec.allowanceInspectionType = initialState.dataRow.allowanceInspectionType;
        rec.allowanceInspectionTime = initialState.dataRow.allowanceInspectionTime;
        rec.monthlyAllowanceInspectionDays = initialState.dataRow.monthlyAllowanceInspectionDays;
        rec.allowanceAmount = initialState.dataRow.allowanceAmount;
        rec.ceilingDailyAllowanceInspectionDays = initialState.dataRow.ceilingDailyAllowanceInspectionDays;
        rec.ceilingDailyAllowanceAmount = initialState.dataRow.ceilingDailyAllowanceAmount;
        rec.levelRangeTo = initialState.dataRow.levelRangeTo;
      }

      // 手当区分なしの初期値も設定（なし→日当や月手当に切り替えた時の初期値。取得した値がデフォルト値でないものを設定）
      if (rec.allowanceType === CONSTANTS.ALLOWANCE_TYPE.NONE.code) {
        rec.allowanceInspectionType = initialState.dataRow.allowanceInspectionType;
        rec.levelRangeTo = initialState.dataRow.levelRangeTo;
      }
      for (let i = CONSTANTS.CCUS_LEVEL_MIN; i <= CONSTANTS.CCUS_LEVEL_MAX; i++) {
        if (rec["ccusAllowanceType" + i] === CONSTANTS.ALLOWANCE_TYPE.NONE.code) {
          rec["ccusAllowanceInspectionType" + i] = initialState.dataRow["ccusAllowanceInspectionType" + i];
          rec["ccusLevelRangeTo" + i] = initialState.dataRow["ccusLevelRangeTo" + i];
        }
      }

      return Object.assign({}, state, { dataRow: rec })
    },
    /** 登録画面描画時のstate更新 */
    initDataRow: (state) => {
      return Object.assign({}, state, { dataRow: initialState.dataRow })
    },
    /** 技能レベル適用変更時のstate更新 */
    selectCcusFlag: (state, action: PayloadAction<boolean>) => {
      state.ccusFlagSelected = action.payload;
    },
    /** loadingFlgのstate更新 */
    setLoadingFlg: (state, action: PayloadAction<boolean>) => {
      return Object.assign({}, state, { loadingFlg: action.payload })
    },
  }
});

export const {
  initCriteria
  , setQualificationCriteria
  , setDataListOfQualificationMaster
  , prepareMoveToDetail
  , setDataRowOfQualificationMaster
  , initDataRow
  , selectCcusFlag
  , setLoadingFlg
} = qualificationSlice.actions;


//　一覧検索
export const getDataListAsync = (QualificationCriteria: Model.getDataList.Request): AppThunk => async dispatch => {

  try {
    // GET-APIを呼び出します。
    const response = await getRequest(CONSTANTS.URL_QUALIFICATIONS_PRIME_LIST, {
      params: QualificationCriteria
    });
    // 結果をstateに設定します。
    dispatch(setDataListOfQualificationMaster(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

//　詳細画面遷移時
export const getDataDetailAsync = (QualificationCriteria: Model.getDataList.Request): AppThunk => async dispatch => {

  try {
    // GET-APIを呼び出します。 TODO モックを使っているためURLが違う
    const response = await getRequest(CONSTANTS.URL_QUALIFICATIONS_PRIME_LIST, {
      params: QualificationCriteria
    });
    if (response.data.data.total > 1) {
      dispatch(setApiResult({ status: response.status, errorCode: "", requestMethod: CONSTANTS.REQUEST_METHOD_GET, url: response.config.url }));
      return;
    }
    if (response.data.data.total === 0) {
      dispatch(setApiResult({ status: response.status, errorCode: "EA9999000", requestMethod: CONSTANTS.REQUEST_METHOD_GET, url: response.config.url }));
      return;
    }
    // 結果をstateに設定します。
    dispatch(setDataRowOfQualificationMaster(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

export const putDataRowAsync = (putData: Model.putDataList.Request): AppThunk => async dispatch => {

  try {
    // PUT-APIを呼び出します。
    const response = await putRequest(CONSTANTS.URL_QUALIFICATIONS_PRIME_INFO, putData);
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_PUT));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_PUT));
  }
};

export const deleteDataLsitAsync = (deleteData: Model.deleteData.Request): AppThunk => async dispatch => {

  try {
    // DELETE-APIを呼び出します。
    const response = await deleteRequest(CONSTANTS.URL_QUALIFICATIONS_PRIME_INFO, { data: deleteData });
    if (response.data.cmn.errorCode === "EA1003001") {
      const message = messagesUtils.getMessage("EA1003001");
      messagesUtils.setMessageInSession(message, CONSTANTS.MESSAGE_TYPE_ERROR);
      dispatch(setApiResult({ status: response.status, errorCode: "", requestMethod: CONSTANTS.REQUEST_METHOD_DELETE, url: response.config.url }));
      return;
    }
    if (response.data.cmn.errorCode === "EA1003001") {
      dispatch(setApiResult({ status: response.status, errorCode: "EA1003001", requestMethod: CONSTANTS.REQUEST_METHOD_DELETE, url: response.config.url }));
      return;
    }
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_DELETE));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_DELETE));
  }
};

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const storedDataList = (state: RootState) => state.primeQualification.dataList;
export const storedDataRow = (state: RootState) => state.primeQualification.dataRow;
export const storedCriteria = (state: RootState) => state.primeQualification.criteria;
export const storedTotal = (state: RootState) => state.primeQualification.total;
export const storedCcusFlagSelected = (state: RootState) => state.primeQualification.ccusFlagSelected;
export const storedLoadingFlg = (state: RootState) => state.primeQualification.loadingFlg

export default qualificationSlice.reducer;
